import type { FieldValues, Path } from 'react-hook-form';

import { TypeCheckbox } from '../checkbox';
import { Hvc } from '../components';
import { TypeInput } from '../input';
import { TypeDateInput } from '../input-date';
import { TypeDateTimeInput } from '../input-date/date-time';
import { TypeInputSearch } from '../input-search';
import { TypeInputSearchNew } from '../input-search/index-new';
import TypeMultiSelect from '../multi-select';
import OTPInput from '../otp';
import { TypeRadio } from '../radio';
import { TypeSelect } from '../select';
import { TypeTextArea } from '../text-area';
import ToolTip from '../tool-tip';
import type { IFormBuilder } from './utils';

const FormBuilder = <T extends FieldValues>({ formComponent, hookForm, min }: IFormBuilder<T>) => (
	<>
		{formComponent?.map((i) => (
			<Hvc
				removeDOM
				view={!i?.hide}
				key={i?.id}
				className={i?.className}
			>
				{i?.component === 'input' && i?.type?.toLowerCase() !== 'date' && (
					<TypeInput
						{...hookForm?.register?.(i?.id as Path<T>)}
						label={i?.label}
						placeholder={i?.placeHolder}
						type={i?.type}
						error={hookForm?.formState?.errors?.[i?.id as Path<T>]?.message as string}
						isonlyview={i?.isonlyview}
						disabled={i?.disabled || i?.isonlyview}
						className={i?.className}
						min={i?.min}
						minComponent={min}
						// TypeInput has supported both of these all along; FormBuilder never passed them,
						// so every field built through here was silently unable to mark itself required
						// or carry helper text in its own footer.
						isRequired={i?.isRequired}
						helperText={i?.helperText}
						autoComplete='off'
					/>
				)}
				{i?.component === 'input' && i?.type?.toLowerCase() === 'date' && (
					<TypeDateInput
						{...hookForm?.register?.(i?.id as Path<T>)}
						label={i?.label}
						placeholder={i?.placeHolder}
						type={i?.type}
						error={hookForm?.formState?.errors?.[i?.id as Path<T>]?.message as string}
						dateOnChange={(date) => {
							hookForm?.setValue?.(i?.id as Path<T>, date as any);
							i?.onDateChange?.(date);
						}}
						dateValue={hookForm?.watch?.(i?.id as Path<T>)}
						isonlyview={i?.isonlyview}
						disabled={i?.disabled || i?.isonlyview}
						className={i?.className}
						min={i?.min}
						minComponent={min}
						autoComplete='off'
					/>
				)}
				{i?.component === 'date-time' && (
					<TypeDateTimeInput
						id={i?.id}
						name={i?.id}
						label={i?.label}
						isRequired={i?.isRequired}
						helperText={i?.helperText}
						error={hookForm?.formState?.errors?.[i?.id as Path<T>]?.message as string}
						// A Date in, a Date out — the field's value IS a Date, so nothing here parses or
						// formats. Conversion happens once at the wire edge, not per call site.
						dateValue={(hookForm?.watch?.(i?.id as Path<T>) as Date | null) ?? null}
						dateOnChange={(date) => {
							hookForm?.setValue?.(i?.id as Path<T>, date as any, { shouldValidate: true });
							i?.onDateChange?.(date);
						}}
						minDate={i?.minDate}
						maxDate={i?.maxDate}
						timeIntervals={i?.timeIntervals}
						isonlyview={i?.isonlyview}
						disabled={i?.disabled || i?.isonlyview}
						className={i?.className}
						minComponent={min}
					/>
				)}
				{i?.component === 'otp' && (
					<OTPInput
						onChange={(otp) => {
							hookForm?.setValue?.(i?.id as Path<T>, otp as any);
						}}
						label={i?.label}
						error={hookForm?.formState?.errors?.[i?.id as Path<T>]?.message as string}
					/>
				)}
				{i?.component === 'select' && (
					<TypeSelect
						// `initOptions` is optional on IFormComponent but REQUIRED by TypeSelect, which
						// dereferences it unconditionally — so the `!` here was a lie, and any caller
						// that omitted it white-screened the page through the error boundary rather
						// than rendering a select. (The Contacts filter drawer did exactly that, for
						// its whole life.) Default it instead of asserting.
						initoption={i?.initOptions ?? { label: 'Select', value: '' }}
						optionsdata={i?.optionData}
						// Opt-in, and deliberately not defaulted true: most selects in this app are
						// filters whose init option means "Any", where blank is a real answer.
						disableInit={i?.disableInit}
						customwidth='100%'
						label={i?.label}
						// TypeSelect renders `htmlFor={props.id || props.name}` and FormBuilder passed
						// neither, which orphaned the label on every select in the app.
						id={i?.id}
						name={i?.id}
						refreshProps={i?.refreshProps}
						error={hookForm?.formState?.errors?.[i?.id as Path<T>]?.message as string}
						onChange={(e) => {
							const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
								hookForm?.setError?.(i?.id as Path<T>, {
									message: '',
								});
								if (!e.target.value) {
									hookForm?.setError?.(i?.id as Path<T>, {
										message: `${i?.id} is missing`,
									});
								}
								hookForm?.setValue?.(i?.id as Path<T>, e.target.value as any);
							};
							if (typeof i?.onPauseChange === 'function') {
								i?.onPauseChange?.(() => {
									handleChange(e);
								});
							} else {
								handleChange(e);
							}
						}}
						value={hookForm?.watch?.(i?.id as Path<T>)}
						className={i?.className ?? ''}
						minComponent={min}
						autoComplete='off'
					/>
				)}
				{i?.component === 'multi-select' && (
					<TypeMultiSelect
						initoption={i?.initOptions!}
						optionsdata={i?.optionData}
						selectedItems={i?.selectedItems!}
						setSelectedItems={i?.setSelectedItems}
						customwidth='100%'
						label={i?.label}
						refreshProps={i?.refreshProps}
						error={hookForm?.formState?.errors?.[i?.id as Path<T>]?.message as string}
						className={i?.className ?? ''}
						hideSelected={i.hideSelected}
						minComponent={min}
						autoComplete='off'
					/>
				)}
				{i?.component === 'input-search-new' && (
					<TypeInputSearchNew
						label={i?.label}
						placeholder={i?.placeHolder}
						min={i?.min}
						minComponent={min}
						autoComplete='off'
						multiSelect={i?.inputSearchMultiSelect}
						selected={hookForm?.watch?.(i?.id as Path<T>)}
						setSelected={(selected) => {
							hookForm?.setValue?.(i?.id as Path<T>, selected as any);
							if (!selected.length) return;
							i?.searchOnSelected?.(selected);
						}}
						onClear={i.searchOnClear}
						load={i?.searchLoad}
						fetchResults={i.searchFetchResults}
						options={i.searchOptions}
						onRemoveValue={i?.searchOnRemoveValue}
						stopTypeSearch={i?.searchStopTypeSearch}
						onAddNew={i?.onAddNew}
						key={
							// i?.searchOptions?.length &&
							!i?.inputSearchMultiSelect ? hookForm?.watch?.(i?.id as Path<T>) : ''
						}
					/>
				)}
				{i?.component === 'input-search' && (
					<TypeInputSearch
						{...hookForm?.register?.(i?.id as Path<T>)}
						searchProps={{
							results: i?.inputSearchProps?.searchProps?.results,
							setSearchTerm: (value, id) => {
								i?.inputSearchProps?.searchProps?.setSearchTerm('');
								i?.inputSearchProps?.searchProps?.setSelected(value);
								i?.inputSearchProps?.setSearchTerm?.(value, id);
							},
							load: i?.inputSearchProps?.load,
						}}
						label={i?.label}
						placeholder={i?.placeHolder}
						value={i?.inputSearchProps?.searchProps?.searchTerm}
						refreshProps={i?.refreshProps}
						selectedItems={i?.inputSearchSelectedItems}
						setSelectedItems={i?.setInputSearchSelectedItems}
						min={i?.min}
						minComponent={min}
						multiSelect={i?.inputSearchMultiSelect}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							i?.inputSearchProps?.searchProps?.setSearchTerm(e.target.value);
							i?.inputSearchProps?.searchProps?.setSelected(e.target.value);
							i?.inputSearchOnChange?.(e);
						}}
						clear={() => {
							i?.inputSearchProps?.searchProps?.setSearchTerm('');
							i?.inputSearchProps?.searchProps?.setSelected('');
							i?.inputSearchProps?.inputSearchClear?.();
						}}
						autoComplete='off'
					/>
				)}
				{i?.component === 'text-area' && (
					<TypeTextArea
						{...hookForm?.register?.(i?.id as Path<T>)}
						placeholder={i?.placeHolder}
						label={i?.label}
						error={hookForm?.formState?.errors?.[i?.id as Path<T>]?.message as string}
						className={i?.className}
						autoComplete='off'
					/>
				)}
				{i?.component === 'check-box' && (
					<div className='f-row-10 align-items-center'>
						<TypeCheckbox
							{...hookForm?.register?.(i?.id as Path<T>)}
							error={hookForm?.formState?.errors?.[i?.id as Path<T>]?.message as string}
							autoComplete='off'
						/>
						<label className='m-0 text-little color-label'>{i?.label}</label>
						{i.description && <ToolTip tip={i.description} />}
					</div>
				)}
				{i?.component === 'radio' && (
					<div className='f-column-17'>
						<div className='f-row-11 align-items-center'>
							<label
								htmlFor={i?.id}
								className='m-0'
							>
								{i?.label}
							</label>
							{i?.description ? <ToolTip tip={i?.description} /> : null}
						</div>
						<div className={i?.radioClass ?? 'f-column-33'}>
							{i?.radioOptions?.map((j, jindex) => (
								<div
									className='f-row-10 align-items-center'
									key={j.label + jindex}
								>
									<TypeRadio
										id={j.id}
										value={j.id}
										{...hookForm?.register?.(i?.id as Path<T>)}
										error={hookForm?.formState?.errors?.[i?.id as Path<T>]?.message as string}
									/>
									<label className='m-0 text-little color-label'>{j.label}</label>
								</div>
							))}
						</div>
					</div>
				)}
			</Hvc>
		))}
	</>
);

export default FormBuilder;
